import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";

import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { Button, Card, CardBody, CardHeader, Col, Container, Input, Label, Row, Spinner, Table } from "../../../Components/ui";
import ToggleSwitch from "../../../Components/ui/ToggleSwitch";
import {
  CategorySetting,
  NotificationDeliveryCheckResponse,
  NotificationCategory,
  NotificationChannel,
  NotificationHistoryItem,
  notificationCenterService,
} from "../../../api/services/notification-center.service";
import AlimTalkAccountSettings from "./AlimTalkAccountSettings";

type TabId = "send" | "history" | "system" | "channels";

const categoryMeta: Array<{ value: NotificationCategory; label: string; description: string }> = [
  { value: "payment", label: "결제 관련", description: "결제 완료·실패·취소·환불 안내" },
  { value: "usage", label: "이용관련", description: "예약·입장·티켓 및 바우처 이용 안내" },
  { value: "site_notice", label: "지점 공지", description: "운영시간·휴장·시설 및 현장 안내" },
  { value: "marketing", label: "마케팅", description: "혜택·쿠폰·이벤트·프로모션 안내" },
  { value: "mandatory", label: "필수 고지", description: "보안·약관·법적 고지" },
];

const channelLabel: Record<NotificationChannel, string> = {
  web_push: "Web Push",
  app_push: "App Push",
  alimtalk: "알림톡",
};

const tabItems: Array<{ id: TabId; label: string }> = [
  { id: "send", label: "통합 발송" },
  { id: "history", label: "통합 내역" },
  { id: "system", label: "시스템 메시지 설정" },
  { id: "channels", label: "채널 설정" },
];

const getInitialTab = (): TabId => {
  const requested = new URLSearchParams(window.location.search).get("tab");
  return tabItems.some((item) => item.id === requested) ? (requested as TabId) : "send";
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString("ko-KR");
};

const parseRecipientIds = (value: string) => {
  const ids = value
    .split(/[\s,]+/)
    .map((item) => Number(item.trim()))
    .filter((item) => Number.isInteger(item) && item > 0);
  return [...new Set(ids)];
};

export default function NotificationCenter() {
  const [activeTab, setActiveTab] = useState<TabId>(getInitialTab);
  const [settings, setSettings] = useState<CategorySetting[]>([]);
  const [history, setHistory] = useState<NotificationHistoryItem[]>([]);
  const [historyCount, setHistoryCount] = useState(0);
  const [historyCategory, setHistoryCategory] = useState<NotificationCategory | "">("");
  const [historySearch, setHistorySearch] = useState("");
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [sending, setSending] = useState(false);
  const [checkingDelivery, setCheckingDelivery] = useState(false);
  const [webPushConfigured, setWebPushConfigured] = useState<boolean | null>(null);
  const [recipientText, setRecipientText] = useState("");
  const [category, setCategory] = useState<NotificationCategory>("site_notice");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [webUrl, setWebUrl] = useState("");
  const [appUrl, setAppUrl] = useState("");
  const [deliveryCheck, setDeliveryCheck] = useState<NotificationDeliveryCheckResponse | null>(null);
  const [recipientChannels, setRecipientChannels] = useState<Record<number, NotificationChannel[]>>({});
  const [alimtalkTemplateId, setAlimtalkTemplateId] = useState<number | null>(null);

  const loadSettings = useCallback(async () => {
    setLoadingSettings(true);
    try {
      const response = await notificationCenterService.getSiteSettings();
      setSettings(response.data.settings);
    } catch (error: any) {
      toast.error(error?.message || "시스템 메시지 설정을 불러오지 못했습니다.");
    } finally {
      setLoadingSettings(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const response = await notificationCenterService.getHistory({
        category: historyCategory || undefined,
        search: historySearch || undefined,
      });
      setHistory(response.data.results);
      setHistoryCount(response.data.count);
    } catch (error: any) {
      toast.error(error?.message || "알림 내역을 불러오지 못했습니다.");
    } finally {
      setLoadingHistory(false);
    }
  }, [historyCategory, historySearch]);

  const loadWebPushConfig = useCallback(async () => {
    try {
      const response = await notificationCenterService.getWebPushConfig();
      setWebPushConfigured(response.data.is_configured);
    } catch {
      setWebPushConfigured(false);
    }
  }, []);

  // 서버 응답 전·실패 때도 잠김으로 본다(발송 0건, 결정 262).
  const [sendLocked, setSendLocked] = useState(true);
  useEffect(() => {
    let active = true;
    notificationCenterService.getSendLock()
      .then((response) => { if (active) setSendLocked(response.data.locked); })
      .catch(() => { if (active) setSendLocked(true); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    void loadSettings();
    void loadWebPushConfig();
  }, [loadSettings, loadWebPushConfig]);

  useEffect(() => {
    if (activeTab === "history") void loadHistory();
  }, [activeTab, loadHistory]);

  const changeTab = (tab: TabId) => {
    setActiveTab(tab);
    const params = new URLSearchParams(window.location.search);
    params.set("tab", tab);
    window.history.replaceState(null, "", `${window.location.pathname}?${params.toString()}`);
  };

  const updateSetting = (categoryValue: NotificationCategory, field: keyof CategorySetting, value: boolean) => {
    setSettings((current) => current.map((item) => (
      item.category === categoryValue ? { ...item, [field]: value } : item
    )));
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const response = await notificationCenterService.updateSiteSettings({ settings });
      setSettings(response.data.settings);
      toast.success("시스템 메시지 설정이 저장되었습니다.");
    } catch (error: any) {
      toast.error(error?.message || "시스템 메시지 설정 저장에 실패했습니다.");
    } finally {
      setSavingSettings(false);
    }
  };

  const resetDeliveryCheck = () => {
    setDeliveryCheck(null);
    setRecipientChannels({});
  };

  const checkDelivery = async () => {
    const recipientIds = parseRecipientIds(recipientText);
    if (!recipientIds.length) {
      toast.warning("수신자 고객 ID를 입력해 주세요.");
      return;
    }
    setCheckingDelivery(true);
    try {
      const response = await notificationCenterService.checkDelivery({
        recipient_ids: recipientIds,
        category,
        alimtalk_template_id: alimtalkTemplateId,
      });
      setDeliveryCheck(response.data);
      setRecipientChannels(Object.fromEntries(response.data.recipients.map((recipient) => [
        recipient.id,
        (Object.keys(channelLabel) as NotificationChannel[]).filter((channel) => recipient.channels[channel].available),
      ])));
      if (response.data.recipients.some((recipient) => !recipient.site_registered)) {
        toast.error("현재 지점에 등록되지 않은 고객은 발송 대상에서 제외됩니다.");
      }
    } catch (error: any) {
      toast.error(error?.message || "수신 가능 여부를 확인하지 못했습니다.");
    } finally {
      setCheckingDelivery(false);
    }
  };

  const toggleRecipientChannel = (recipientId: number, channel: NotificationChannel) => {
    setRecipientChannels((current) => {
      const selected = current[recipientId] || [];
      return {
        ...current,
        [recipientId]: selected.includes(channel)
          ? selected.filter((item) => item !== channel)
          : [...selected, channel],
      };
    });
  };

  const send = async () => {
    if (!deliveryCheck) {
      toast.warning("수신 가능 여부를 먼저 확인해 주세요.");
      return;
    }
    if (!title.trim() || !body.trim()) {
      toast.warning("제목과 내용을 입력해 주세요.");
      return;
    }
    const selectedRecipients = deliveryCheck.recipients.filter((recipient) => (
      recipient.site_registered && (recipientChannels[recipient.id] || []).length > 0
    ));
    if (!selectedRecipients.length) {
      toast.warning("발송할 수신자와 채널을 하나 이상 선택해 주세요.");
      return;
    }
    const selectedChannels = [...new Set(selectedRecipients.flatMap((recipient) => recipientChannels[recipient.id] || []))];
    if (selectedChannels.includes("alimtalk") && sendLocked) {
      toast.error("발송 잠김(시작 전) — 대표님 '시작' 뒤 열립니다. 알림톡 채널을 빼면 앱 알림은 보낼 수 있습니다.");
      return;
    }
    if (selectedChannels.includes("alimtalk") && !alimtalkTemplateId) {
      toast.warning("알림톡 승인 템플릿을 선택해 주세요.");
      return;
    }
    setSending(true);
    try {
      const response = await notificationCenterService.send({
        recipient_ids: selectedRecipients.map((recipient) => recipient.id),
        category,
        title: title.trim(),
        body: body.trim(),
        web_url: webUrl.trim(),
        app_url: appUrl.trim(),
        channels: selectedChannels,
        recipient_channel_overrides: selectedRecipients.map((recipient) => ({
          recipient_id: recipient.id,
          channels: recipientChannels[recipient.id] || [],
        })),
        alimtalk_template_id: alimtalkTemplateId,
      });
      toast.success(`${response.data.created}명에게 알림을 생성했습니다.`);
      setRecipientText("");
      setTitle("");
      setBody("");
      setWebUrl("");
      setAppUrl("");
      resetDeliveryCheck();
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || error?.message || "알림 발송에 실패했습니다.");
    } finally {
      setSending(false);
    }
  };

  const categoryLabel = useMemo(() => Object.fromEntries(categoryMeta.map((item) => [item.value, item.label])), []);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="알림 센터" pageTitle="알림&메시지" />

        <Card>
          <CardHeader className="border-0 pb-0">
            <div className="d-flex flex-wrap gap-2" role="tablist" aria-label="알림 센터 메뉴">
              {tabItems.map((item) => (
                <Button
                  key={item.id}
                  type="button"
                  color={activeTab === item.id ? "primary" : "soft-secondary"}
                  onClick={() => changeTab(item.id)}
                >
                  {item.label}
                </Button>
              ))}
            </div>
          </CardHeader>
          <CardBody>
            {activeTab === "send" ? (
              <Row className="g-4">
                <Col xl={8}>
                  <div className="mb-3">
                    <Label htmlFor="notification-recipients">수신자 고객 ID</Label>
                    <Input id="notification-recipients" value={recipientText} onChange={(event) => { setRecipientText(event.target.value); resetDeliveryCheck(); }} placeholder="예: 101, 102, 103" />
                    <div className="form-text">현재 지점에 등록된 고객만 발송 대상에 포함됩니다. 입력 뒤 수신 가능 여부를 확인해 채널을 개별 선택하세요.</div>
                  </div>
                  <div className="mb-3">
                    <Label htmlFor="notification-category">메시지 분류</Label>
                    <Input id="notification-category" type="select" value={category} onChange={(event) => { setCategory(event.target.value as NotificationCategory); setAlimtalkTemplateId(null); resetDeliveryCheck(); }}>
                      {categoryMeta.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                    </Input>
                  </div>
                  <div className="mb-3">
                    <Label htmlFor="notification-title">제목</Label>
                    <Input id="notification-title" value={title} onChange={(event) => setTitle(event.target.value)} maxLength={200} />
                  </div>
                  <div className="mb-3">
                    <Label htmlFor="notification-body">내용</Label>
                    <Input id="notification-body" type="textarea" rows={6} value={body} onChange={(event) => setBody(event.target.value)} maxLength={5000} />
                  </div>
                  <Row className="g-3">
                    <Col md={6}>
                      <Label htmlFor="notification-web-url">Web 이동 경로</Label>
                      <Input id="notification-web-url" value={webUrl} onChange={(event) => setWebUrl(event.target.value)} placeholder="/notification 또는 https://..." />
                    </Col>
                    <Col md={6}>
                      <Label htmlFor="notification-app-url">App 이동 경로</Label>
                      <Input id="notification-app-url" value={appUrl} onChange={(event) => setAppUrl(event.target.value)} placeholder="amazingcamp://..." />
                    </Col>
                  </Row>
                </Col>
                <Col xl={4}>
                  <div className="rounded border p-3 h-100">
                    <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
                      <h5 className="mb-0">수신자별 채널 선택</h5>
                      <Button color="soft-primary" size="sm" onClick={() => void checkDelivery()} disabled={checkingDelivery}>
                        {checkingDelivery ? <><Spinner size="sm" className="me-1" />확인 중</> : "수신 가능 여부 확인"}
                      </Button>
                    </div>
                    {!deliveryCheck ? <p className="text-muted small mb-0">Web Push, App Push, 알림톡을 수신자별로 선택하려면 먼저 확인하세요.</p> : null}
                    {deliveryCheck ? (
                      <>
                        <div className="border rounded p-2 mb-3 small">
                          {(Object.keys(channelLabel) as NotificationChannel[]).map((channel) => {
                            const channelStatus = deliveryCheck.site_channels[channel];
                            return <div key={channel} className="mb-1"><strong>{channelLabel[channel]}</strong> · {channelStatus.site_enabled && channelStatus.configured ? "사용 가능" : channelStatus.reason || "설정 확인 필요"}</div>;
                          })}
                        </div>
                        <div className="mb-3">
                          <Label htmlFor="alimtalk-template">알림톡 승인 템플릿</Label>
                          <Input id="alimtalk-template" type="select" value={alimtalkTemplateId || ""} onChange={(event) => setAlimtalkTemplateId(event.target.value ? Number(event.target.value) : null)}>
                            <option value="">선택 안 함</option>
                            {deliveryCheck.alimtalk_templates.map((template) => <option key={template.id} value={template.id}>{template.name} ({template.code})</option>)}
                          </Input>
                          <div className="form-text">템플릿을 바꾼 경우 수신 가능 여부를 다시 확인하세요.</div>
                        </div>
                        <div className="table-responsive border rounded">
                          <Table className="align-middle mb-0 small">
                            <thead><tr><th>수신자</th>{(Object.keys(channelLabel) as NotificationChannel[]).map((channel) => <th className="text-center" key={channel}>{channelLabel[channel]}</th>)}</tr></thead>
                            <tbody>
                              {deliveryCheck.recipients.map((recipient) => (
                                <tr key={recipient.id}>
                                  <td>
                                    {recipient.site_registered ? <>{recipient.name || `고객 ${recipient.id}`}<br /><small className="text-muted">{recipient.phone_num || recipient.reason || "-"}</small></> : <span className="text-danger">고객 {recipient.id}<br /><small>{recipient.reason}</small></span>}
                                  </td>
                                  {(Object.keys(channelLabel) as NotificationChannel[]).map((channel) => {
                                    const channelCheck = recipient.channels[channel];
                                    const selected = (recipientChannels[recipient.id] || []).includes(channel);
                                    return (
                                      <td className="text-center" key={channel}>
                                        <Input className="form-check-input m-0" type="checkbox" aria-label={`${recipient.id} ${channelLabel[channel]} 사용`} checked={selected} disabled={!channelCheck.available} onChange={() => toggleRecipientChannel(recipient.id, channel)} />
                                        {!channelCheck.available ? <div className="text-muted mt-1" style={{ fontSize: 11 }}>{channelCheck.reason}</div> : null}
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </>
                    ) : null}
                    {sendLocked ? <p role="status" className="mt-3 mb-0 rounded border border-warning p-2 text-warning">발송 잠김(시작 전) — 대표님 '시작' 뒤 알림톡이 열립니다. 앱 알림만 보낼 수 있습니다.</p> : null}
                    <Button color="primary" className="w-100 mt-4" onClick={() => void send()} disabled={sending || !deliveryCheck}>
                      {sending ? <><Spinner size="sm" className="me-2" />발송 중</> : "알림 발송"}
                    </Button>
                  </div>
                </Col>
              </Row>
            ) : null}

            {activeTab === "history" ? (
              <>
                <div className="d-flex flex-wrap gap-2 mb-3">
                  <Input type="select" value={historyCategory} onChange={(event) => setHistoryCategory(event.target.value as NotificationCategory | "")} style={{ maxWidth: 180 }}>
                    <option value="">전체 분류</option>
                    {categoryMeta.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                  </Input>
                  <Input value={historySearch} onChange={(event) => setHistorySearch(event.target.value)} placeholder="고객명, 전화번호, 제목 검색" style={{ maxWidth: 280 }} />
                  <Button color="soft-primary" onClick={() => void loadHistory()} disabled={loadingHistory}>조회</Button>
                  <span className="align-self-center text-muted">총 {historyCount}건</span>
                </div>
                <div className="table-responsive">
                  <Table className="align-middle mb-0">
                    <thead><tr><th>발송 시각</th><th>수신자</th><th>분류</th><th>제목</th><th>채널 결과</th><th>읽음</th></tr></thead>
                    <tbody>
                      {loadingHistory ? <tr><td colSpan={6} className="text-center py-4"><Spinner size="sm" /></td></tr> : null}
                      {!loadingHistory && history.length === 0 ? <tr><td colSpan={6} className="text-center py-4 text-muted">발송 이력이 없습니다.</td></tr> : null}
                      {history.map((item) => (
                        <tr key={item.id}>
                          <td className="text-nowrap">{formatDateTime(item.created_at)}</td>
                          <td>{item.user_name || "-"}<br /><small className="text-muted">{item.phone_num || ""}</small></td>
                          <td>{categoryLabel[item.category]}</td>
                          <td><strong>{item.title}</strong><br /><small className="text-muted text-break">{item.body}</small></td>
                          <td>{item.deliveries.length ? item.deliveries.map((delivery) => <div key={delivery.id}>{channelLabel[delivery.channel]} · {delivery.state}</div>) : "알림함"}</td>
                          <td>{item.read_at ? formatDateTime(item.read_at) : "미확인"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </>
            ) : null}

            {activeTab === "system" ? (
              <>
                {loadingSettings ? <div className="text-center py-4"><Spinner size="sm" /></div> : null}
                <Row className="g-3">
                  {categoryMeta.map((meta) => {
                    const setting = settings.find((item) => item.category === meta.value);
                    if (!setting) return null;
                    const mandatory = meta.value === "mandatory";
                    return (
                      <Col lg={6} key={meta.value}>
                        <div className="border rounded p-3 h-100">
                          <div className="d-flex justify-content-between gap-3 mb-2">
                            <div><h5 className="mb-1">{meta.label}</h5><p className="text-muted mb-0">{meta.description}</p></div>
                            <ToggleSwitch checked={setting.is_enabled} disabled={mandatory} onChange={(value) => updateSetting(meta.value, "is_enabled", value)} />
                          </div>
                          <div className="d-flex flex-wrap gap-3 mt-3">
                            {(Object.keys(channelLabel) as NotificationChannel[]).map((channel) => {
                              const field = `${channel}_enabled` as keyof CategorySetting;
                              return <ToggleSwitch key={channel} label={channelLabel[channel]} checked={Boolean(setting[field])} disabled={mandatory && channel !== "alimtalk"} onChange={(value) => updateSetting(meta.value, field, value)} />;
                            })}
                          </div>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
                <div className="d-flex justify-content-end mt-4"><Button color="primary" onClick={() => void saveSettings()} disabled={savingSettings}>{savingSettings ? "저장 중" : "설정 저장"}</Button></div>
              </>
            ) : null}

            {activeTab === "channels" ? (
              <>
                <Row className="g-3">
                  <Col lg={4}><div className="border rounded p-3 h-100"><h5>Web Push</h5><p className="mb-0">{webPushConfigured === null ? "상태 확인 중" : webPushConfigured ? "사용 가능" : "VAPID 설정 필요"}</p></div></Col>
                  <Col lg={4}><div className="border rounded p-3 h-100"><h5>App Push</h5><p className="mb-0">Expo 토큰 기반 발송</p></div></Col>
                  <Col lg={4}><div className="border rounded p-3 h-100"><h5>알림톡</h5><p className="mb-0">지점 연결과 전송 가능 상태를 아래에서 확인</p></div></Col>
                </Row>
                <div className="mt-3">
                  <AlimTalkAccountSettings />
                </div>
              </>
            ) : null}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
}
